/**
 * API Keys management page.
 */

import React, { useEffect, useState } from 'react';
import { getApiKeys, createApiKey, deleteApiKey, toggleApiKey } from '../services';
import { ApiKey, CreateApiKeyResult } from '../types';
import {
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Copy,
  CheckCircle2,
} from 'lucide-react';

export default function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState<CreateApiKeyResult | null>(null);

  // create form
  const [keyName, setKeyName] = useState('');
  const [env, setEnv] = useState<'production' | 'staging' | 'development'>('development');
  const [creating, setCreating] = useState(false);

  const [copied, setCopied] = useState(false);

  const load = () => {
    getApiKeys()
      .then(setKeys)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async () => {
    setCreating(true);
    const result = await createApiKey(keyName, env);
    setNewKeyResult(result);
    setCreating(false);
    setShowCreate(false);
    setKeyName('');
    load();
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDelete = async (id: string) => {
    await deleteApiKey(id);
    load();
  };

  const handleToggle = async (id: string) => {
    await toggleApiKey(id);
    load();
  };

  if (loading) return <div className="page-loader">Loading API keys…</div>;

  return (
    <div className="apikeys-page">
      <div className="page-header">
        <div>
          <h1>API Keys</h1>
          <p>Manage your developer API keys for SDK integration</p>
        </div>
        <button className="btn btn-accent" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> New Key
        </button>
      </div>

      {/* ── Newly created key banner ─────────────────────── */}
      {newKeyResult && (
        <div className="alert alert-success key-reveal">
          <CheckCircle2 size={16} />
          <div>
            <strong>API Key Created</strong>
            <p className="key-warn">Copy your key now — it won't be shown again.</p>
            <div className="key-display">
              <code>{newKeyResult.fullKey}</code>
              <button className="btn btn-ghost btn-sm" onClick={() => handleCopy(newKeyResult.fullKey)}>
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setNewKeyResult(null)}>Dismiss</button>
        </div>
      )}

      {/* ── Create modal ─────────────────────────────────── */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create API Key</h2>
            <div className="form-group">
              <label>Key Name</label>
              <input placeholder="e.g. Production Backend" value={keyName} onChange={(e) => setKeyName(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Environment</label>
              <select value={env} onChange={(e) => setEnv(e.target.value as typeof env)}>
                <option value="development">Development</option>
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={handleCreate} disabled={creating || !keyName}>
                {creating ? 'Creating…' : 'Create Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Keys table ───────────────────────────────────── */}
      <div className="table-container">
        <table className="data-table apikeys-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Key Prefix</th>
              <th>Environment</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last Used</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td className="cell-primary">{k.name}</td>
                <td><code>{k.keyPrefix}…</code></td>
                <td><span className={`env-badge env-${k.environment}`}>{k.environment}</span></td>
                <td className="key-status-cell">
                  <span className={`status-dot ${k.isActive ? 'active' : 'inactive'}`} />
                  {k.isActive ? 'Active' : 'Inactive'}
                </td>
                <td>{new Date(k.createdAt).toLocaleDateString()}</td>
                <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : '—'}</td>
                <td className="actions-cell">
                  <div className="apikeys-actions">
                    <button
                      className={`btn-icon key-toggle-btn ${k.isActive ? 'is-active' : 'is-inactive'}`}
                      title={k.isActive ? 'Deactivate' : 'Activate'}
                      onClick={() => handleToggle(k.id)}
                    >
                      {k.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                    </button>
                    <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(k.id)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={7} className="empty-state">No API keys yet. Create one to get started.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
